import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["aria-label", "ariaLabel", "children", "disabled", "focused", "hovered", "id", "Icon", "keyboardFocused", "onBlur", "onClick", "onFocus", "onMouseDown", "onMouseOut", "onMouseOver", "onMouseUp", "onPress", "onPressIn", "onPressOut", "onTouchEnd", "onTouchStart", "pressed", "tabIndex", "testID", "UNSAFE_touchableProps", "UNSAFE_iconProps", "colorToken", "colorTokenArgs", "isBig", "isSmall"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React from 'react';
import { Touchable } from '@bubble-ui/primitive-touchable';
import { View } from '@bubble-ui/primitive-view';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import { useTouchableControl } from '@bubble-ui/provider-touchable-control';
import { useInteractiveStateCombinedWithOverrides } from '@bubble-ui/hook-use-interactive-state';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { usePropDeprecationWarning } from '@bubble-ui/hook-use-prop-deprecation-warning';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { getIds } from './getIds';
import { defaultComponentTokens } from './defaultComponentTokens';
var TouchableIcon = _ref => {
  var {
      'aria-label': ariaLabel,
      ariaLabel: ariaLabelDeprecated,
      children,
      disabled,
      focused,
      hovered,
      id,
      Icon,
      keyboardFocused,
      onBlur,
      onClick,
      onFocus,
      onMouseDown,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onPress,
      onPressIn,
      onPressOut,
      onTouchEnd,
      onTouchStart,
      pressed,
      tabIndex,
      testID,
      UNSAFE_touchableProps,
      UNSAFE_iconProps,
      //props only used by getDefaultStyleSheet
      colorToken,
      colorTokenArgs,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isBig,
      isSmall

      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  usePropsSpreadingDeprecationWarning({
    componentName: 'TouchableIcon',
    props,
    useInstead: 'UNSAFE_touchableProps',
    removalDate: 'v7.0.0'
  });
  usePropDeprecationWarning({
    componentName: 'TouchableIcon',
    propName: 'Icon',
    shouldWarn: Icon !== undefined,
    useInstead: 'children',
    removalDate: 'v8.0.0'
  });
  var designContext = useDesignContext(defaultComponentTokens);
  var {
    turnOffTouchability,
    touchablesInteractiveState
  } = useTouchableControl();
  var [interactiveStateCombined, eventHandlers] = useInteractiveStateCombinedWithOverrides(_objectSpread(_objectSpread({}, props), {}, {
    disabled,
    focused,
    hovered,
    keyboardFocused,
    onFocus,
    onBlur,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPressIn,
    onPressOut,
    onTouchEnd,
    onTouchStart,
    pressed
  }), touchablesInteractiveState);

  // if no icon was provided, there's no reason to keep going
  if (!children && !Icon) {
    return null;
  }

  // we use the getDefaultStyleSheet directly since this component
  // can't be themed or overriden with StylesheetOverridesProvider
  var styleSheet = getDefaultStyleSheet({
    colorToken,
    colorTokenArgs,
    designContext,
    disabled: interactiveStateCombined.disabled,
    turnOffTouchability,
    interactiveState: interactiveStateCombined.interactiveState,
    isSmall
  });
  var {
    ids
  } = getIds({
    id
  });
  var touchableIconChildren = /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.overlay,
    style: styleSheet.overlayStyle
  }, interactiveStateCombined.keyboardFocused && /*#__PURE__*/React.createElement(View, {
    style: styleSheet.focusedBorderStyle
  }), /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.iconWrapper,
    style: styleSheet.iconWrapperStyle
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, children ? (/*#__PURE__*/React.cloneElement(children, _objectSpread(_objectSpread({}, UNSAFE_iconProps), {}, {
    colorToken: styleSheet.iconColorToken,
    colorTokenArgs: styleSheet.iconColorTokenArgs
  }))) : Icon ? /*#__PURE__*/React.createElement(Icon, _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.icon
  }, UNSAFE_iconProps, {
    colorToken: styleSheet.iconColorToken,
    colorTokenArgs: styleSheet.iconColorTokenArgs
  })) : null)));
  return /*#__PURE__*/React.createElement(View, {
    style: styleSheet.wrapperStyle
  }, turnOffTouchability ? /*#__PURE__*/React.createElement(View, _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.root,
    testID: testID
  }, UNSAFE_touchableProps, {
    style: styleSheet.containerStyle
  }, props), touchableIconChildren) : /*#__PURE__*/React.createElement(Touchable, _extends({
    id: ids === null || ids === void 0 ? void 0 : ids.root,
    testID: testID
  }, UNSAFE_touchableProps, {
    "aria-label": ariaLabel || ariaLabelDeprecated,
    style: styleSheet.containerStyle,
    title: ariaLabel || ariaLabelDeprecated,
    type: "button",
    accessibilityState: {
      disabled: interactiveStateCombined.disabled
    },
    disabled: interactiveStateCombined.disabled,
    onClick: onClick,
    onPress: onPress,
    tabIndex: tabIndex
  }, props, eventHandlers), touchableIconChildren));
};
TouchableIcon.displayName = 'TouchableIcon';
export { TouchableIcon };
//# sourceMappingURL=index.js.map